import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { turkishIncludes } from '../lib/turkishSearch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Upload, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Loader2,
  Building2,
  Calendar,
  Users,
  Search,
  Save,
  ClipboardList
} from 'lucide-react';
import api from '../lib/api';
import { cn } from '../lib/utils';

const LoonbriefUpload = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  
  const [firms, setFirms] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]); // Multi-file support
  const [autoDetectPeriod, setAutoDetectPeriod] = useState(true); // Auto detect period from PDF
  
  // Manual match dialog
  const [matchDialog, setMatchDialog] = useState({ open: false, index: null, searchQuery: '' });
  
  // Save summary dialog
  const [saveSummary, setSaveSummary] = useState({ open: false, data: null });
  
  const fileInputRef = useRef(null);
  
  // Generate period options (last 12 months)
  const periodOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('tr-TR', { year: 'numeric', month: 'long' });
    periodOptions.push({ value, label });
  }
  
  const fetchFirms = useCallback(async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setFirms(response.data.data || []);
        // Auto-select for firm admin
        if (user?.role === 'FIRM_ADMIN' && user?.firm_id) {
          setSelectedFirm(user.firm_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch firms', error);
    }
  }, [user?.role, user?.firm_id]);
  
  const fetchUsersWithAliases = useCallback(async () => {
    try {
      const response = await api.get(`/loonbrief/users-with-aliases?firm_id=${selectedFirm}`);
      if (response.data.success) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  }, [selectedFirm]);

  useEffect(() => {
    fetchFirms();
  }, [fetchFirms]);
  
  useEffect(() => {
    if (selectedFirm) {
      fetchUsersWithAliases();
    }
  }, [selectedFirm, fetchUsersWithAliases]);;
  
  // Handle file upload - sends to backend for processing (supports multiple files)
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    if (!selectedFirm) {
      toast.error(t('loonbrief.selectFirmFirst'));
      return;
    }
    
    // If not auto-detecting period, require manual selection
    if (!autoDetectPeriod && !selectedPeriod) {
      toast.error(t('loonbrief.selectPeriodFirst'));
      return;
    }
    
    setProcessing(true);
    setUploadResults([]);
    setUploadedFiles(files);
    
    try {
      let allResults = [];
      
      // Process each file separately
      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        const file = files[fileIndex];
        
        // Create FormData for backend
        const formData = new FormData();
        formData.append('file', file);
        formData.append('firm_id', selectedFirm);
        formData.append('period', autoDetectPeriod ? '' : selectedPeriod); // Empty = auto-detect from PDF
        formData.append('auto_detect_period', autoDetectPeriod ? 'true' : 'false');
        
        // Send to backend for PDF processing
        const response = await api.post('/loonbrief/process-pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          const results = response.data.data.results || [];
          // Add file info to each result
          const resultsWithFile = results.map(r => ({
            ...r,
            file_index: fileIndex,
            file_name: file.name
          }));
          allResults = [...allResults, ...resultsWithFile];
        } else {
          toast.error(`${file.name}: ${response.data.message || t('loonbrief.processError')}`);
        }
      }
      
      setUploadResults(allResults);
      
      // Show summary
      const matchedCount = allResults.filter(r => r.status === 'matched').length;
      const unmatchedCount = allResults.filter(r => r.status === 'unmatched').length;
      
      if (files.length === 1) {
        toast.success(`${t('loonbrief.processSuccess')}: ${matchedCount} ${t('loonbrief.matched')}, ${unmatchedCount} ${t('loonbrief.unmatched')}`);
      } else {
        toast.success(`${files.length} dosya işlendi: ${matchedCount} ${t('loonbrief.matched')}, ${unmatchedCount} ${t('loonbrief.unmatched')}`);
      }
      
    } catch (error) {
      console.error('PDF processing error:', error);
      toast.error(error.response?.data?.detail || t('loonbrief.processError'));
    } finally {
      setProcessing(false);
    }
  };
  
  // Manual match
  const handleManualMatch = (userId) => {
    const selectedUser = users.find(u => u.id === userId);
    if (selectedUser && matchDialog.index !== null) {
      setUploadResults(prev => {
        const updated = [...prev];
        updated[matchDialog.index] = {
          ...updated[matchDialog.index],
          matched_user_id: selectedUser.id,
          matched_user_name: selectedUser.full_name,
          status: 'matched'
        };
        return updated;
      });
      toast.success(`${selectedUser.full_name} ${t('loonbrief.matchSuccess')}`);
    }
    setMatchDialog({ open: false, index: null, searchQuery: '' });
  };
  
  // Save matched results to backend
  const handleSaveMatched = async () => {
    const matchedResults = uploadResults.filter(r => r.status === 'matched' && r.matched_user_id);
    const unmatchedResults = uploadResults.filter(r => r.status === 'unmatched');
    
    if (matchedResults.length === 0) {
      toast.error(t('loonbrief.noMatchedToSave'));
      return;
    }
    
    if (uploadedFiles.length === 0) {
      toast.error(t('loonbrief.pdfNotFound'));
      return;
    }
    
    setSaving(true);
    
    try {
      let allSavedDetails = [];
      let allErrorDetails = [];
      
      // Group results by file_index
      const resultsByFile = {};
      matchedResults.forEach(r => {
        const fileIdx = r.file_index || 0;
        if (!resultsByFile[fileIdx]) {
          resultsByFile[fileIdx] = [];
        }
        resultsByFile[fileIdx].push(r);
      });
      
      // Process each file
      for (const [fileIdxStr, fileResults] of Object.entries(resultsByFile)) {
        const fileIdx = parseInt(fileIdxStr);
        const file = uploadedFiles[fileIdx];
        
        if (!file) continue;
        
        const filePeriod = fileResults[0]?.period || selectedPeriod;
        
        const matchedPages = fileResults.map(r => ({
          page: r.page_number,
          user_id: r.matched_user_id,
          period: r.period || filePeriod
        }));
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('firm_id', selectedFirm);
        formData.append('period', filePeriod);
        formData.append('matched_pages', JSON.stringify(matchedPages));
        
        const response = await api.post('/loonbrief/save-matched', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.success) {
          const data = response.data.data;
          allSavedDetails.push(...(data.saved_details || []));
          allErrorDetails.push(...(data.error_details || []));
        } else {
          allErrorDetails.push(...fileResults.map(r => ({
            page: r.page_number,
            user_name: r.matched_user_name || '?',
            reason: response.data.message || 'Bilinmeyen hata'
          })));
        }
      }
      
      // Build summary data
      const summaryData = {
        saved: allSavedDetails,
        errors: allErrorDetails,
        unmatched: unmatchedResults.map(r => ({
          page: r.page_number,
          name: r.employee_name_from_pdf,
          file_name: r.file_name
        }))
      };
      
      // Show summary modal
      setSaveSummary({ open: true, data: summaryData });
      
      // Clear results after save
      setUploadResults([]);
      setUploadedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || t('loonbrief.saveError'));
    } finally {
      setSaving(false);
    }
  };
  
  // Filter users for manual match dialog
  const filteredUsersForMatch = users.filter(u => {
    if (!matchDialog.searchQuery) return true;
    const query = matchDialog.searchQuery;
    return turkishIncludes(u.full_name || '', query) ||
           (u.aliases || []).some(a => turkishIncludes(a, query)) ||
           turkishIncludes(u.naam || '', query) ||
           turkishIncludes(u.voornaam || '', query);
  });
  
  const matchedCount = uploadResults.filter(r => r.status === 'matched').length;
  const unmatchedCount = uploadResults.filter(r => r.status === 'unmatched').length;
  const errorCount = uploadResults.filter(r => r.status === 'error').length;
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" data-testid="loonbrief-title">
            {t('loonbrief.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('loonbrief.description')}
          </p>
        </div>
      </div>
      
      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            {t('loonbrief.uploadSettings')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Firm Select */}
            <div className="space-y-2">
              <Label>{t('loonbrief.firmRequired')}</Label>
              <Select value={selectedFirm} onValueChange={setSelectedFirm} data-testid="firm-select">
                <SelectTrigger>
                  <SelectValue placeholder={t('loonbrief.selectFirm')} />
                </SelectTrigger>
                <SelectContent>
                  {firms.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Period Select with Auto-detect option */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{autoDetectPeriod ? 'Dönem (PDF\'den)' : t('loonbrief.periodRequired')}</Label>
                <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoDetectPeriod}
                    onChange={(e) => setAutoDetectPeriod(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-300"
                  />
                  Otomatik
                </label>
              </div>
              {autoDetectPeriod ? (
                <div className="h-10 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500 flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  PDF'den otomatik algılanacak
                </div>
              ) : (
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod} data-testid="period-select">
                  <SelectTrigger>
                    <SelectValue placeholder={t('loonbrief.selectPeriod')} />
                  </SelectTrigger>
                  <SelectContent>
                    {periodOptions.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Upload Button - Multiple files */}
            <div className="space-y-2">
              <Label>{t('loonbrief.pdfUpload')} (Çoklu)</Label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileUpload}
                  disabled={!selectedFirm || (!autoDetectPeriod && !selectedPeriod) || processing}
                  className="cursor-pointer"
                  data-testid="pdf-upload-input"
                />
              </div>
            </div>
          </div>
          
          {/* Info */}
          {selectedFirm && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <Users className="h-4 w-4 inline mr-2" />
              <strong>{users.length}</strong> {t('loonbrief.employeesLoaded')}. 
              {users.filter(u => u.aliases?.length > 0).length > 0 && (
                <span className="ml-2">
                  ({users.filter(u => u.aliases?.length > 0).length} {t('loonbrief.employeesWithAliases')})
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Processing indicator */}
      {processing && (
        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-slate-600 dark:text-slate-300">{t('loonbrief.processing')}</p>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Results Table */}
      {uploadResults.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-emerald-600" />
                {t('loonbrief.results')}
              </CardTitle>
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {matchedCount} {t('loonbrief.statusMatched')}
                </Badge>
                <Badge variant="outline" className="bg-amber-50 text-amber-700">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {unmatchedCount} {t('loonbrief.statusUnmatched')}
                </Badge>
                {errorCount > 0 && (
                  <Badge variant="outline" className="bg-red-50 text-red-700">
                    <XCircle className="h-3 w-3 mr-1" />
                    {errorCount} {t('loonbrief.statusError')}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800">
                    {uploadedFiles.length > 1 && (
                      <TableHead className="w-32">Dosya</TableHead>
                    )}
                    <TableHead className="w-16">{t('loonbrief.page')}</TableHead>
                    <TableHead>{t('loonbrief.nameFromPdf')}</TableHead>
                    <TableHead>{t('loonbrief.matchedEmployee')}</TableHead>
                    <TableHead className="w-28">Dönem</TableHead>
                    <TableHead className="w-24">{t('loonbrief.status')}</TableHead>
                    <TableHead className="w-32">{t('loonbrief.action')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploadResults.map((result, idx) => (
                    <TableRow key={idx} className={cn(
                      result.status === 'matched' && 'bg-emerald-50/50',
                      result.status === 'unmatched' && 'bg-amber-50/50',
                      result.status === 'error' && 'bg-red-50/50'
                    )} data-testid={`result-row-${idx}`}>
                      {uploadedFiles.length > 1 && (
                        <TableCell className="text-xs text-slate-500 max-w-[120px] truncate" title={result.file_name}>
                          {result.file_name}
                        </TableCell>
                      )}
                      <TableCell className="font-mono">{result.page_number}</TableCell>
                      <TableCell className="font-medium">{result.employee_name_from_pdf}</TableCell>
                      <TableCell>
                        {result.matched_user_name ? (
                          <span className="text-emerald-700">{result.matched_user_name}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {result.period ? (
                          <Badge variant="outline" className="font-mono text-xs">
                            {result.period}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {result.status === 'matched' && (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {t('loonbrief.statusMatched')}
                          </Badge>
                        )}
                        {result.status === 'unmatched' && (
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {t('loonbrief.statusUnmatched')}
                          </Badge>
                        )}
                        {result.status === 'error' && (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                            <XCircle className="h-3 w-3 mr-1" />
                            {t('loonbrief.statusError')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {result.status !== 'matched' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMatchDialog({ open: true, index: idx, searchQuery: '' })}
                            data-testid={`manual-match-btn-${idx}`}
                          >
                            {t('loonbrief.manualMatch')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Save Button */}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadResults([]);
                  setUploadedFiles([]);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
                data-testid="clear-results-btn"
              >
                {t('loonbrief.clear')}
              </Button>
              <Button
                onClick={handleSaveMatched}
                disabled={saving || matchedCount === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-matched-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {t('loonbrief.saveMatched')} ({matchedCount})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Manual Match Dialog */}
      <Dialog open={matchDialog.open} onOpenChange={(open) => !open && setMatchDialog({ open: false, index: null, searchQuery: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('loonbrief.manualMatchTitle')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {matchDialog.index !== null && uploadResults[matchDialog.index] && (
              <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-300">{t('loonbrief.nameInPdf')}</p>
                <p className="font-semibold">{uploadResults[matchDialog.index].employee_name_from_pdf}</p>
              </div>
            )}
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('loonbrief.searchEmployee')}
                value={matchDialog.searchQuery}
                onChange={(e) => setMatchDialog(prev => ({ ...prev, searchQuery: e.target.value }))}
                className="pl-10"
                data-testid="manual-match-search"
              />
            </div>
            
            <div className="max-h-60 overflow-y-auto space-y-1">
              {filteredUsersForMatch.map(u => (
                <button
                  key={u.id}
                  onClick={() => handleManualMatch(u.id)}
                  className="w-full text-left p-2 rounded hover:bg-emerald-50 transition-colors"
                  data-testid={`match-user-${u.id}`}
                >
                  <p className="font-medium">{u.full_name}</p>
                  {u.aliases?.length > 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('loonbrief.aliases')}: {u.aliases.join(', ')}
                    </p>
                  )}
                  {(u.naam || u.voornaam) && (
                    <p className="text-xs text-slate-400">
                      {t('loonbrief.naam')}: {u.naam} {u.voornaam}
                    </p>
                  )}
                </button>
              ))}
              {filteredUsersForMatch.length === 0 && (
                <p className="text-center text-slate-400 py-4">{t('loonbrief.employeeNotFound')}</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchDialog({ open: false, index: null, searchQuery: '' })}>
              {t('loonbrief.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Summary Dialog */}
      <AlertDialog open={saveSummary.open} onOpenChange={(open) => !open && setSaveSummary({ open: false, data: null })}>
        <AlertDialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="save-summary-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-600" />
              {t('loonbrief.saveSummaryTitle') || 'Kaydetme Sonucu'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('loonbrief.saveSummaryDesc') || 'Bordro kaydetme islemi tamamlandi.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {saveSummary.data && (
            <div className="space-y-4 py-2">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-center" data-testid="summary-saved-count">
                  <div className="text-2xl font-bold text-emerald-700">{saveSummary.data.saved?.length || 0}</div>
                  <div className="text-xs text-emerald-600 mt-1">{t('loonbrief.summarySaved') || 'Kaydedildi'}</div>
                </div>
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-center" data-testid="summary-error-count">
                  <div className="text-2xl font-bold text-red-700">{saveSummary.data.errors?.length || 0}</div>
                  <div className="text-xs text-red-600 mt-1">{t('loonbrief.summaryErrors') || 'Hata'}</div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-center" data-testid="summary-unmatched-count">
                  <div className="text-2xl font-bold text-amber-700">{saveSummary.data.unmatched?.length || 0}</div>
                  <div className="text-xs text-amber-600 mt-1">{t('loonbrief.summaryUnmatched') || 'Eslesemedi'}</div>
                </div>
              </div>
              
              {/* Saved Details */}
              {saveSummary.data.saved?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-emerald-700 mb-2 flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4" />
                    {t('loonbrief.summarySuccessTitle') || 'Basariyla Kaydedilen Bordrolar'}
                  </h4>
                  <div className="rounded-md border border-emerald-200 divide-y divide-emerald-100 bg-white max-h-40 overflow-y-auto">
                    {saveSummary.data.saved.map((item, idx) => (
                      <div key={idx} className="px-3 py-2 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-slate-400">S.{item.page}</span>
                          <span className="font-medium text-slate-800">{item.user_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs font-mono">{item.period}</Badge>
                          {item.action === 'updated' && (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">{t('loonbrief.summaryUpdated') || 'Guncellendi'}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Error Details */}
              {saveSummary.data.errors?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-red-700 mb-2 flex items-center gap-1.5">
                    <XCircle className="h-4 w-4" />
                    {t('loonbrief.summaryErrorTitle') || 'Hatali Kayitlar'}
                  </h4>
                  <div className="rounded-md border border-red-200 divide-y divide-red-100 bg-white max-h-40 overflow-y-auto">
                    {saveSummary.data.errors.map((item, idx) => (
                      <div key={idx} className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-slate-400">S.{item.page}</span>
                          <span className="font-medium text-red-700">{item.user_name || item.user_id}</span>
                        </div>
                        <p className="text-xs text-red-500 mt-0.5">{item.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Unmatched Details */}
              {saveSummary.data.unmatched?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-amber-700 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" />
                    {t('loonbrief.summaryUnmatchedTitle') || 'Eslestirilemeyenler'}
                  </h4>
                  <div className="rounded-md border border-amber-200 divide-y divide-amber-100 bg-white max-h-40 overflow-y-auto">
                    {saveSummary.data.unmatched.map((item, idx) => (
                      <div key={idx} className="px-3 py-2 flex items-center gap-2 text-sm">
                        <span className="font-mono text-xs text-slate-400">S.{item.page}</span>
                        <span className="font-medium text-amber-800">{item.name}</span>
                        {item.file_name && (
                          <span className="text-xs text-slate-400 ml-auto truncate max-w-[120px]" title={item.file_name}>
                            {item.file_name}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-2">
                    {t('loonbrief.unmatchedHint') || 'Bu calisanlar sisteme kayitli olmayabilir veya isimleri farkli formatta olabilir. Elle eslestirme yapabilirsiniz.'}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogAction data-testid="summary-close-btn">
              {t('loonbrief.close') || 'Kapat'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoonbriefUpload;
